import { isAccountOwner } from '@hotjar/account';
import type { SiteAccount } from '@hotjar/account';
import { useSubscriptionInfo } from '@hotjar/subscription-management';
import { useCurrentUser } from '@hotjar/user';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';

interface UseAnnualBillingEligibility {
  isLoading: boolean;
  isEligible: boolean;
}

/**
 * Takes an account as input and returns whether or not that account is eligible for annual billing.
 *
 * Eligibility is determined as follows:
 *
 *   * Billing v2 accounts are always eligible
 *   * Billing v3 accounts are only eligible if they are not monthly customers already
 *
 * Note: eligibility doesn't imply the account can actually make changes to its billing period, it just tells whether
 * or not annual billing is available to them as a feature. For example, it will return `true` (eligible) for a billing
 * v2 account that's already on an annual subscription, or for a v3 account on an annual subscription.
 */
export const useAnnualBillingEligibility = (
  account: Pick<SiteAccount, 'id' | 'features' | 'adminIds'> | undefined,
): UseAnnualBillingEligibility => {
  const { isLoading: isLoadingUser, data: currentUser } = useCurrentUser();

  /**
   * Whether or not the current user is the owner of the account received through the `account` prop.
   *
   * Using `usePermission('payment-methods-manage')` would've been neat, but it naively assumes that we want to run
   * the check against the current site's account, which might not be true for all cases, especially in the upselling
   * domain.
   */
  const isOwner = isAccountOwner(currentUser?.id, account);
  const isBillingV3 = isBillingV3Enabled(account);

  /**
   * Currently, we can't specify to `useSubscriptionInfo` which account we want to receive subscription info about. It
   * always assumes we want data about the current user's owned account. This will be a problem once we want to make
   * the annual initiative support non-owners on Billing 3.0.
   */
  const { isLoading: isLoadingSubscription, data: subscription } = useSubscriptionInfo({
    enabled: isBillingV3 && isOwner,
  });

  const hasV3Subscription = !!subscription?.customer?.subscription;
  const isLoading = isLoadingUser || isLoadingSubscription;
  const isEligible =
    (isOwner &&
      isBillingV3 &&
      hasV3Subscription &&
      subscription.customer?.subscription.billingPeriod === 'ANNUALLY') ||
    (isOwner && isBillingV3 && !hasV3Subscription) ||
    (isOwner && !isBillingV3);

  return {
    isLoading,
    isEligible,
  };
};
